import { Component, HostListener, OnInit } from '@angular/core';
import { MonitoringService } from '@services/monitoring.service';
import { NavigationService } from '@services/navigation.service';
import { AuthService } from '@services/auth.service';
import { SignalRService } from '@services/signalr.service';
import { UserNotification } from '@models/userNotification.model';
import { UserNotificationService } from '@services/user-notification.service';
import { ActiveComponentService } from '@services/active-component.service';
import { StudyBlockUiService } from '@shared/study-block-ui/study-block-ui.service';
import { delay } from 'rxjs/operators';
import { ConfigService } from '@services/config.service';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { BrowserTabVisibilityChangeService } from './services/browser-tab-visibility-change.service';
import { PrimeNGConfig } from 'primeng/api';
import { ReportSignalRService } from '@services/reportsignalr.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  title = 'EY TP Benchmark';
  public uiBlocked$: any;
  public isDisplayFooter = false;
  public currentYear: number = new Date().getFullYear();

  constructor(
    private monitoringService: MonitoringService,
    private authService: AuthService,
    public navigationService: NavigationService,
    private signalRService: SignalRService,
    private userNotificationService: UserNotificationService,
    private activeComponentService: ActiveComponentService,
    private configService: ConfigService,
    private studyBlockUiService: StudyBlockUiService,
    private router: Router,
    private browserTabVisibilityChangeService: BrowserTabVisibilityChangeService,
    private primengConfig: PrimeNGConfig,
    private reportSignalRService: ReportSignalRService,
  ) {
    this.uiBlocked$ = this.studyBlockUiService.uiBlocked$.asObservable().pipe(delay(1));
  }

  @HostListener('document:visibilitychange', ['$event'])
  setCurrentTabAsActive() {
    this.browserTabVisibilityChangeService.setCurrentTabAsActive();
  }

  @HostListener('window:beforeunload', ['$event'])
  removeTabFromReventlyActive() {
    this.browserTabVisibilityChangeService.removeTabFromRecentlyActive();
  }

  public async ngOnInit() {
    this.primengConfig.ripple = true;
    this.browserTabVisibilityChangeService.resetTabUniqueId();
    await this.authService.setupConfig();
    const buildVersionNumber = <string>await this.navigationService.GetAPIBuildVersion();
    this.monitoringService.setupMonitoring(buildVersionNumber);

    this.subscribeToSignalR();
    this.subscribeToReportSignalR();
    this.configService.loadInitialConfigValues();

    this.router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.studyBlockUiService.blockUI();
          break;
        }
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.studyBlockUiService.unblockUI();
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  private subscribeToSignalR() {
    this.signalRService.init();

    this.signalRService.addNotificationReceived.subscribe((notification: UserNotification) => {
      this.userNotificationService.addNotification(notification.message, notification.isErrorMessage, notification.url);
    });

    this.signalRService.updateNotificationReceived.subscribe((notification: UserNotification) => {
      this.userNotificationService.addUpdateNotification(notification.key, notification.message, notification.isErrorMessage);
    });
  }

  private subscribeToReportSignalR() {
    this.reportSignalRService.init();
    this.reportSignalRService.addNotificationReceived.subscribe((notification: UserNotification) => {
      this.userNotificationService.addReportNotification(
        notification.key,
        notification.message,
        notification.isErrorMessage,
        notification.url,
        notification.isRequestCompleted,
      );
    });
  }

  public toggleFullScreenGrid() {
    this.navigationService.gridExpanded = !this.navigationService.gridExpanded;
    this.navigationService.isGridExtended.emit(this.navigationService.gridExpanded);
  }

  public resetFullScreenGrid() {
    this.navigationService.gridExpanded = false;
  }

  onActivate($event) {
    this.resetFullScreenGrid();
    this.isDisplayFooter = false;
    this.activeComponentService.activeComponent = $event;
    if ($event.isFooterDisplayFlag !== undefined && $event.isFooterDisplayFlag === true) {
      this.isDisplayFooter = true;
    }
  }
}
